import utils from "./firebaseUtils";
import { collection, doc, updateDoc, getDoc, onSnapshot } from "firebase/firestore";
import {
  FIREBASE_COLLECTION_GAMES,
  FIREBASE_FUNCTION_GENERATE_NARRATION,
  FIREBASE_FUNCTION_GENERATE_AUDIO,
  FIREBASE_FUNCTION_GENERATE_ALL_QUESTION_NARRATIONS,
  FIREBASE_FUNCTION_GENERATE_CONVERSATIONAL_NARRATION,
  FIREBASE_FUNCTION_GENERATE_ALL_CATEGORY_AUDIO,
  FIREBASE_FUNCTION_CREATE_FINAL_REVEAL_CONVERSATION,
  FIREBASE_COLLECTION_NARRATIONS,
} from "./constants";
import { loadDiffOfModelsForQuerySnapshot } from "./firebaseModels";

import logging from "utils/logging";

const log = logging.getLogger("firebase/host");
log.setLevel('info');

/**
 * Requests narration generation for a specific game state and context
 * @param {string} gameId - The game ID
 * @param {string} narrationKey - The narration key
 * @param {object} state - The game state
 * @param {object} context - Additional context
 * @returns {Promise<object>} - Success response from cloud function
 */
const requestNarration = async (gameId, narrationKey, momentType, state, context) => {
  log.info("Requesting narration for game", gameId, "with key", narrationKey);
  
  const generateNarrationFunc = utils.getCallableFunction(FIREBASE_FUNCTION_GENERATE_NARRATION);
  
  try {
    const result = await generateNarrationFunc({
      gameId, 
      narrationKey, 
      momentType,
      state, 
      context
    });
    
    log.info("Generated narration with result:", result);
    return result.data;
  } catch (error) {
    log.error("Error generating narration:", error);
    throw error;
  }
};

/**
 * Requests audio generation for an existing narration
 * @param {string} gameId - The game ID
 * @param {string} narrationKey - The narration key to generate audio for
 * @returns {Promise<object>} - Success response from cloud function
 */
const requestAudio = async (gameId, narrationKey) => {
  log.info("Requesting audio generation for game", gameId, "with key", narrationKey);
  
  const generateAudioFunc = utils.getCallableFunction(FIREBASE_FUNCTION_GENERATE_AUDIO);
  
  try {
    const result = await generateAudioFunc({
      gameId,
      narrationKey
    });
    
    log.info("Generated audio with result:", result);
    return result.data;
  } catch (error) {
    log.error("Error generating audio:", error);
    throw error;
  }
};

/**
 * Updates the host status directly (without going through cloud function)
 * @param {string} gameId - The game ID 
 * @param {string} status - The new host status
 * @returns {Promise<void>}
 * @deprecated Use updateHostState instead for atomic updates
 */
const updateHostStatus = async (gameId, status) => {
  log.info("DEPRECATED: Use updateHostState instead for atomic updates");
  const gameRef = doc(utils.firestore(), FIREBASE_COLLECTION_GAMES, gameId);
  
  await updateDoc(gameRef, {
    'hostState.status': status,
    'hostState.lastUpdated': utils.firestore().FieldValue.serverTimestamp()
  });
  
  log.info(`Updated host status for game ${gameId} to ${status}`);
};

/**
 * Gets the current host state for a game
 * @param {string} gameId - The game ID
 * @returns {Promise<object|null>} - The host state or null if not found
 */
const getHostState = async (gameId) => {
  const gameRef = doc(utils.firestore(), FIREBASE_COLLECTION_GAMES, gameId);
  
  try {
    const gameDoc = await getDoc(gameRef);
    if (!gameDoc.exists()) {
      log.warn(`Game ${gameId} not found`);
      return null;
    }
    
    const gameData = gameDoc.data();
    return gameData.hostState || null;
  } catch (error) {
    log.error(`Error getting host state for game ${gameId}:`, error);
    throw error;
  }
};

/**
 * Sets the current narration key in the host state
 * This determines which narration the host is currently focused on
 * @param {string} gameId - The game ID
 * @param {string} narrationKey - The narration key to set as current
 * @returns {Promise<void>}
 * @deprecated Use updateHostState instead for atomic updates
 */
const setCurrentNarrationKey = async (gameId, narrationKey) => {
  log.info("DEPRECATED: Use updateHostState instead for atomic updates");
  log.info(`Setting current narration key for game ${gameId} to ${narrationKey}`);
  
  const gameRef = doc(utils.firestore(), FIREBASE_COLLECTION_GAMES, gameId);
  
  await updateDoc(gameRef, {
    'hostState.currentNarrationKey': narrationKey,
    'hostState.lastUpdated': utils.firestore().FieldValue.serverTimestamp()
  });
  
  log.info(`Updated current narration key for game ${gameId} to ${narrationKey}`);
};

/**
 * Updates the host state in a single transaction
 * @param {string} gameId - The game ID
 * @param {object} updates - Object with host state updates
 * @param {string} [updates.status] - Host status to set
 * @param {string} [updates.currentNarrationKey] - Current narration key to set
 * @param {object} [updates.narrationUpdates] - Updates to apply to a specific narration
 * @param {string} [updates.narrationUpdates.narrationKey] - Key of narration to update
 * @param {object} [updates.narrationUpdates.updates] - Fields to update on the narration
 * @returns {Promise<void>}
 */
const updateHostState = async (gameId, updates) => {
  log.info(`Updating host state for game ${gameId}:`, updates);
  
  const gameRef = doc(utils.firestore(), FIREBASE_COLLECTION_GAMES, gameId);
  
  // Create update object
  const updateObj = {};
  
  // Add host status if provided
  if (updates.status) {
    updateObj['hostState.status'] = updates.status;
  }
  
  // Add current narration key if provided
  if (updates.currentNarrationKey !== undefined) {
    updateObj['hostState.currentNarrationKey'] = updates.currentNarrationKey;
  }
  
  log.debug('About to update host state object: ', updateObj);

  await updateDoc(gameRef, updateObj);
  
  log.info(`Updated host state for game ${gameId}`);
};

const updateNarration = async (gameId, narrationId, updates) => {
  const narrationRef = doc(utils.firestore(), FIREBASE_COLLECTION_GAMES, gameId, FIREBASE_COLLECTION_NARRATIONS, narrationId);
  await updateDoc(narrationRef, updates);
}

/**
 * Watches narrations for a specific game
 * @param {string} gameId - The game ID
 * @param {function} onChange - The function to call when the narrations change
 * @returns {Promise<listener>}
 */
const watchNarrations = async (gameId, onChange) => {
  const narrationsRef = collection(utils.firestore(), FIREBASE_COLLECTION_GAMES, gameId, FIREBASE_COLLECTION_NARRATIONS);

  return onSnapshot(narrationsRef, async (narrationsSnap) => {
    const diff = loadDiffOfModelsForQuerySnapshot(narrationsSnap);
    log.debug("Got updated narrations: ", diff);
    onChange(diff);
  });
};

/**
 * Requests generation of all question narrations for a game
 * @param {string} gameId - The game ID
 * @returns {Promise<object>} - Success response from cloud function
 */
const generateAllQuestionNarrations = async (gameId) => {
  log.info("Requesting generation of all question narrations for game", gameId);
  
  const generateAllQuestionNarrationsFunc = utils.getCallableFunction(FIREBASE_FUNCTION_GENERATE_ALL_QUESTION_NARRATIONS);
  
  try {
    const result = await generateAllQuestionNarrationsFunc({
      gameId
    });
    
    log.info("Generated all question narrations with result:", result);
    return result.data;
  } catch (error) {
    log.error("Error generating all question narrations:", error);
    throw error;
  }
};

/**
 * Requests conversational narration generation (multi-turn, with history)
 * @param {string} gameId
 * @param {string} narrationKey
 * @param {string} momentType
 * @param {object} context
 * @param {string} conversationId
 * @returns {Promise<object>}
 */
const requestConversationalNarration = async (gameId, narrationKey, momentType, context, conversationId) => {
  log.info("Requesting conversational narration for game", gameId, "key", narrationKey, "conversation", conversationId);

  const func = utils.getCallableFunction(FIREBASE_FUNCTION_GENERATE_CONVERSATIONAL_NARRATION);

  try {
    const result = await func({
      gameId,
      narrationKey,
      momentType,
      context,
      conversationId,
    });
    log.info("Generated conversational narration with result:", result);
    return result.data;
  } catch (error) {
    log.error("Error generating conversational narration:", error);
    throw error;
  }
};

const generateAllCategoryAudio = async (gameId) => {
  log.info("Requesting generation of all category audio for game", gameId);

  const generateAllCategoryAudioFunc = utils.getCallableFunction(FIREBASE_FUNCTION_GENERATE_ALL_CATEGORY_AUDIO);

  try {
    const result = await generateAllCategoryAudioFunc({ gameId });
    log.info("Generated all category audio with result:", result);
    return result.data;
  } catch (error) {
    log.error("Error generating all category audio:", error);
    throw error;
  }
};

/**
 * Creates a conversation for the Final Jeopardy reveal sequence.
 * Called once when transitioning to final.revealing.
 * @param {string} gameId
 * @returns {Promise<string>} conversationId
 */
const createFinalRevealConversation = async (gameId) => {
  log.info("Creating final reveal conversation for game", gameId);

  const func = utils.getCallableFunction(FIREBASE_FUNCTION_CREATE_FINAL_REVEAL_CONVERSATION);

  try {
    const result = await func({ gameId });
    log.info("Created final reveal conversation:", result.data);
    return result.data.conversationId;
  } catch (error) {
    log.error("Error creating final reveal conversation:", error);
    throw error;
  }
};

const api = {
  requestNarration,
  requestConversationalNarration,
  requestAudio,
  generateAllQuestionNarrations,
  generateAllCategoryAudio,
  createFinalRevealConversation,
  updateHostStatus,
  getHostState,
  setCurrentNarrationKey,
  updateNarration,
  updateHostState,
  watchNarrations,
};

export default api; 